import { logger } from 'common/services';

import { createBackgroundImageError } from '../backgroundImageError';
import { calculateCoverCrop } from './calculateCoverCrop';
import {
  ACCEPTED_EXTENSIONS,
  ACCEPTED_MIME_TYPES,
  BACKGROUND_HEIGHT,
  BACKGROUND_WIDTH,
  MAX_SOURCE_BYTES,
  THUMBNAIL_HEIGHT,
  THUMBNAIL_WIDTH,
  WEBP_QUALITY,
} from './constants';

import type { CoverCrop } from './calculateCoverCrop';

export type NormalisedBackgroundImage = {
  /** `data:image/webp;base64,…` at exactly BACKGROUND_WIDTH x BACKGROUND_HEIGHT. */
  imageDataUrl: string;
  /** `data:image/webp;base64,…` at exactly THUMBNAIL_WIDTH x THUMBNAIL_HEIGHT. */
  thumbnailDataUrl: string;
  /**
   * The decoded file's own dimensions, EXIF orientation already applied. This is the only moment
   * they are knowable: the output is always the fixed size, so once stored an upscaled 640x360 photo
   * is indistinguishable from a downscaled 4K one.
   */
  sourceWidth: number;
  sourceHeight: number;
};

const isAcceptedType = ({ type, name }: File) => {
  // Some browsers hand back an empty `type` for files dragged from certain sources; fall back to the
  // extension rather than rejecting a perfectly good PNG.
  if (!type) return ACCEPTED_EXTENSIONS.some((extension) => name.toLowerCase().endsWith(extension));

  return (ACCEPTED_MIME_TYPES as readonly string[]).includes(type);
};

const drawToDataUrl = (bitmap: ImageBitmap, crop: CoverCrop, width: number, height: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw createBackgroundImageError('encodeFailed', 'Could not get a 2d canvas context');

  context.drawImage(bitmap, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/webp', WEBP_QUALITY);
  if (!dataUrl.startsWith('data:image/')) throw createBackgroundImageError('encodeFailed');

  // `toDataURL` silently falls back to PNG when the encoder can't do WebP. The background still works
  // — Agora only cares about dimensions — but PNG is several times larger, so it eats the storage
  // budget much faster and the write may be refused. Worth knowing about if it ever shows up.
  if (!dataUrl.startsWith('data:image/webp')) {
    logger.warn('[normaliseBackgroundImage] Browser did not encode WebP; the background will be larger than usual');
  }

  return dataUrl;
};

/**
 * Turns a user-picked image file into the two data URLs we store: the background itself and its
 * picker thumbnail.
 *
 * Deliberately not built on `common/hooks/useImageCompression` (`browser-image-compression`). That
 * library optimises for "get under N MB" and chooses its own output dimensions, which routinely land
 * on odd numbers — the exact case that makes Agora's processor fail silently (see `constants.ts`). It
 * also can't cover-crop to an exact size. Raw canvas gives deterministic, even-by-construction output
 * in a single encode pass.
 */
export const normaliseBackgroundImage = async (file: File): Promise<NormalisedBackgroundImage> => {
  if (!isAcceptedType(file)) throw createBackgroundImageError('unsupportedType');
  if (file.size > MAX_SOURCE_BYTES) throw createBackgroundImageError('tooLarge');

  let bitmap: ImageBitmap;

  try {
    // `imageOrientation: 'from-image'` is not optional. The default is `'none'`, which ignores the EXIF
    // orientation flag and would lay every portrait phone photo on its side.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (error) {
    // Warn, not error: a corrupt or exotic file the user picked isn't an application fault.
    logger.warn('[normaliseBackgroundImage] Could not decode the picked image', {
      reason: error instanceof Error ? error.message : String(error),
    });
    throw createBackgroundImageError('decodeFailed');
  }

  try {
    if (!bitmap.width || !bitmap.height) throw createBackgroundImageError('decodeFailed');

    // Read into locals before anything else touches the bitmap: `close()` in the `finally` zeroes
    // `width`/`height`, and this is the only record of what the user actually picked.
    const { width: sourceWidth, height: sourceHeight } = bitmap;

    const crop = calculateCoverCrop({
      sourceWidth,
      sourceHeight,
      targetWidth: BACKGROUND_WIDTH,
      targetHeight: BACKGROUND_HEIGHT,
    });

    return {
      imageDataUrl: drawToDataUrl(bitmap, crop, BACKGROUND_WIDTH, BACKGROUND_HEIGHT),
      // Same source rect, so the thumbnail frames exactly what the background will.
      thumbnailDataUrl: drawToDataUrl(bitmap, crop, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT),
      sourceWidth,
      sourceHeight,
    };
  } finally {
    bitmap.close();
  }
};
